import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const STATUS_COLORS = {
  doing_well: { color: '#16a34a', bg: '#dcfce7', label: 'Stable', icon: '🟢' },
  slight_change: { color: '#ca8a04', bg: '#fef9c3', label: 'Watch', icon: '🟡' },
  significant_change: { color: '#ea580c', bg: '#ffedd5', label: 'Attention', icon: '🟠' },
  urgent: { color: '#dc2626', bg: '#fee2e2', label: 'Urgent', icon: '🔴' },
};

const CareLogsList = ({ user, onLogout }) => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [clientRes, logsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/clients/${clientId}/care-logs`),
      ]);
      setClient(clientRes.data);
      setLogs(logsRes.data);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [clientId]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this care log?')) return;
    try {
      await axios.delete(`${API}/care-logs/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="loading-screen">Loading…</div>;

  return (
    <div className="dashboard-container" data-testid="care-logs-list">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container">
        <button onClick={() => navigate('/clients')} className="clog-back-btn" data-testid="carelogs-back">
          <i className="fa-solid fa-arrow-left" /> Back to Clients
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginTop: '1rem' }}>
          <div>
            <h1 className="page-title"><i className="fa-solid fa-clipboard-list" style={{ color: '#2c5364', marginRight: 8 }} /> Care Logs</h1>
            <p className="page-subtitle">for <strong>{client?.name}</strong></p>
          </div>
          <Link to={`/clients/${clientId}/care-logs/new`} className="submit-button" style={{ width: 'auto', padding: '0.65rem 1.2rem' }} data-testid="new-carelog-btn">
            <i className="fa-solid fa-plus" /> Start Daily Care Log
          </Link>
        </div>

        {logs.length === 0 && (
          <div className="directory-empty" data-testid="carelogs-empty">
            <i className="fa-solid fa-clipboard-list directory-empty-icon"></i>
            <h3>No care logs yet</h3>
            <p>Start today's daily care log to track {client?.name}'s wellness, vitals, meals, and any incidents.</p>
          </div>
        )}

        {logs.length > 0 && (
          <div className="carelog-list">
            {logs.map(log => {
              const status = STATUS_COLORS[log.overall_status] || { color: '#94a3b8', bg: '#f1f5f9', label: 'Draft', icon: '⚪' };
              return (
                <div key={log.id} className="carelog-item" data-testid={`carelog-item-${log.id}`}>
                  <div className="carelog-item-main">
                    <div className="carelog-status-pill" style={{ background: status.bg, color: status.color }}>
                      {status.icon} {log.status === 'in_progress' ? 'In progress' : status.label}
                    </div>
                    <div>
                      <div className="carelog-date">{log.log_date}</div>
                      <div className="carelog-meta">
                        {log.arrival_time || '--'} → {log.departure_time || '--'}
                        {log.signed_by && <> · Signed by <strong>{log.signed_by}</strong></>}
                        {log.fall_today && <span className="carelog-fall-tag"><i className="fa-solid fa-triangle-exclamation" /> Fall reported</span>}
                      </div>
                    </div>
                  </div>
                  <div className="carelog-item-actions">
                    {log.status === 'in_progress' ? (
                      <Link to={`/clients/${clientId}/care-logs/${log.id}/edit`} className="submit-button" style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.85rem' }} data-testid={`carelog-resume-${log.id}`}>
                        <i className="fa-solid fa-pen" /> Resume
                      </Link>
                    ) : (
                      <Link to={`/clients/${clientId}/care-logs/${log.id}/view`} className="submit-button" style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.85rem', background: '#6b7280' }} data-testid={`carelog-view-${log.id}`}>
                        <i className="fa-solid fa-eye" /> View
                      </Link>
                    )}
                    <button className="delete-button" onClick={() => handleDelete(log.id)} data-testid={`carelog-delete-${log.id}`}>
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CareLogsList;
